/**
 * §A.2 · Las métricas de plataforma.
 *
 * ## Un mes, cinco cifras, y el mes se elige una vez
 *
 * Las cinco rutas toman el mismo `?period=`, así que el selector está arriba y manda
 * sobre todas. Tener un mes por bloque sería invitar a comparar septiembre con
 * agosto sin darse cuenta.
 *
 * ## `404` es «todavía no se ha calculado», no un error
 *
 * Lo decide el servidor a propósito —ver la cabecera de `metricas.ts`— y aquí se
 * pinta como un hueco con su explicación. Cada bloque lo resuelve por su cuenta
 * porque cada métrica es una fila distinta de `platform_metrics`: un mes puede tener
 * MRR y no tener extras.
 *
 * ## El MRR primero, y su desglose pegado
 *
 * «"Subió 300 €" no dice si entraron ocho clubes o si tres subieron de plan y dos se
 * fueron, y esas dos historias piden decisiones opuestas.» Así que la cifra grande
 * nunca sale sola.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, noHayNada } from '@/shared/api/errores';
import { Cargando, Error as PanelError } from '@/shared/ui/Estados';
import { euros, fechaYHora, numero } from '@/shared/ui/formato';
import {
  conSigno,
  etiquetaDeExtra,
  mesAnterior,
  mesEnCurso,
  mesEnPalabras,
  mesSiguiente,
  movimientoNeto,
  nombreDeCanal,
  type Adquisicion,
  type Alumnos,
  type Churn,
  type Extras,
  type Mrr,
} from '@/backoffice/tiposDeMetricas';

/**
 * Una métrica: o los datos, o «este mes no se ha calculado», o el error de verdad.
 *
 * `null` en los datos significa `404`, que es un dato. Se distingue del error porque
 * la diferencia entre «no hay pasada» y «el servidor está caído» cambia lo que hace
 * quien lo lee: esperar a mañana, o llamar a alguien.
 */
function useMetrica<T>(
  nombre: string,
  period: string,
  pedir: () => Promise<{ data: unknown; error: unknown }>
) {
  return useQuery({
    queryKey: ['admin', 'metrica', nombre, period],
    // Reintentar un 404 que es un dato es gastar tres viajes para saber lo mismo.
    retry: false,
    queryFn: async (): Promise<T | null> => {
      const { data, error } = await pedir();
      if (error) {
        const p = aProblema(error);
        if (noHayNada(p)) return null;
        throw new ErrorDeApi(p);
      }
      return data as T;
    },
  });
}

/**
 * El envoltorio de un bloque: título, el hueco del `404` y el error.
 *
 * `silenciarHueco` existe porque las cinco métricas salen de **la misma pasada**: si
 * no ha corrido, las cinco responden `404` y la pantalla repetía cinco veces el mismo
 * párrafo de seis líneas. Eso no es informar, es ruido. Cuando faltan todas se dice
 * una vez arriba; el aviso por bloque queda para el caso que de verdad lo necesita —
 * un mes con MRR y sin extras, que pasa cuando no hubo ni un pedido de material—.
 *
 * Se vio mirando la pantalla, no leyéndola: en el código los cinco `<Bloque>` se leen
 * bien. 18-09-2026.
 */
function Bloque<T>({
  titulo,
  explicacion,
  consulta,
  silenciarHueco,
  children,
}: {
  titulo: string;
  explicacion?: string;
  consulta: ReturnType<typeof useMetrica<T>>;
  silenciarHueco?: boolean;
  children: (datos: T) => React.ReactNode;
}) {
  return (
    <>
      <h2 className="seccion">{titulo}</h2>
      {explicacion && <p className="tenue">{explicacion}</p>}

      {consulta.isPending && (
        <Cargando filas={2} etiqueta={`Cargando ${titulo}`} />
      )}

      {consulta.isError && (
        <PanelError
          problema={aProblema(consulta.error)}
          reintentar={() => void consulta.refetch()}
        />
      )}

      {/*
        `=== null` y no `!consulta.data`: un objeto vacío también es falso y aquí
        significaría lo contrario de lo que se quiere decir.
      */}
      {consulta.data === null && !silenciarHueco && (
        <p className="tenue">
          Esta métrica no tiene pasada de este mes.{' '}
          <strong>No es un cero</strong>: un cero se copiaría a un correo.
        </p>
      )}

      {consulta.data != null && children(consulta.data)}
    </>
  );
}

export function Metricas() {
  const [period, setPeriod] = useState(mesEnCurso);
  const hoy = mesEnCurso();

  const mrr = useMetrica<Mrr>('mrr', period, () =>
    api.v1.admin.metrics.mrr.get({ query: { period } })
  );
  const churn = useMetrica<Churn>('churn', period, () =>
    api.v1.admin.metrics.churn.get({ query: { period } })
  );
  const alumnos = useMetrica<Alumnos>('students', period, () =>
    api.v1.admin.metrics.students.get({ query: { period } })
  );
  const adquisicion = useMetrica<Adquisicion>('acquisition', period, () =>
    api.v1.admin.metrics.acquisition.get({ query: { period } })
  );
  const extras = useMetrica<Extras>('extras', period, () =>
    api.v1.admin.metrics.extras.get({ query: { period } })
  );

  /*
   * `=== null` en las cinco: `null` es «el servidor dice que no hay», distinto de
   * `undefined` —todavía cargando— y de un error. Con `!data` una carga en curso
   * contaría como hueco y el aviso parpadearía al abrir.
   */
  const sinPasada =
    mrr.data === null &&
    churn.data === null &&
    alumnos.data === null &&
    adquisicion.data === null &&
    extras.data === null;

  return (
    <section>
      <div className="titulo-fila">
        <h1>Métricas</h1>
        <div className="hueco-acciones">
          <button
            type="button"
            className="btn-plano"
            onClick={() => setPeriod(mesAnterior(period))}
          >
            ← Mes anterior
          </button>
          <button
            type="button"
            className="btn-plano"
            /* Sin futuro: no hay pasada de un mes que no ha empezado. */
            disabled={period >= hoy}
            onClick={() => setPeriod(mesSiguiente(period))}
          >
            Mes siguiente →
          </button>
        </div>
      </div>

      <p className="mes-suelto">
        <strong>{mesEnPalabras(period)}</strong>
      </p>

      {mrr.data && (
        <p className="tenue">
          Calculado {fechaYHora(mrr.data.computed_at)}.{' '}
          {/* La hora del cálculo importa el día que la cifra no cuadra con otra. */}
          Las cinco salen de la misma pasada de las 02:30.
        </p>
      )}

      {/*
        Las cinco faltan: es **una** cosa que ha pasado —la pasada no ha corrido— y se
        cuenta una vez. Repetirlo en cada bloque era lo que hacía la pantalla antes.
      */}
      {sinPasada && (
        <div className="aviso" role="status">
          <p>
            <strong>Este mes todavía no se ha calculado.</strong> Las cinco
            métricas salen de la misma pasada, que corre a las 02:30: el mes en
            curso aparece a la mañana siguiente.
          </p>
          <p className="tenue">
            El servidor responde que no hay dato en vez de devolver ceros, y es
            a propósito: un cero es una cifra, y una cifra falsa de MRR se copia
            a un correo y ya nadie la comprueba.
          </p>
        </div>
      )}

      <Bloque titulo="MRR" consulta={mrr} silenciarHueco={sinPasada}>
        {(m) => (
          <>
            <dl className="datos datos-grandes">
              <div>
                <dt>Al mes</dt>
                <dd>{euros(m.mrr_cents)}</dd>
              </div>
              <div>
                <dt>Clubes pagando</dt>
                <dd>{m.paying_clubs}</dd>
              </div>
              <div>
                <dt>En prueba</dt>
                {/* Fuera del MRR a propósito: un club en prueba no es ingreso. */}
                <dd>{m.trialing_clubs}</dd>
              </div>
            </dl>

            <p className="tenue">
              Los de prueba no están en el MRR, y es a propósito: contarlos
              infla la única cifra que dice si el producto funciona.
            </p>

            <h3>De dónde viene el movimiento</h3>

            <dl className="datos">
              <div>
                <dt>Nuevo</dt>
                <dd>{conSigno(m.new_cents, euros)}</dd>
              </div>
              <div>
                <dt>Subidas de plan</dt>
                <dd>{conSigno(m.expansion_cents, euros)}</dd>
              </div>
              <div>
                <dt>Bajadas de plan</dt>
                {/* En negativo aunque el servidor la manda en positivo: así la columna suma. */}
                <dd>{conSigno(m.contraction_cents, euros, true)}</dd>
              </div>
              <div>
                <dt>Se fueron</dt>
                <dd>{conSigno(m.churned_cents, euros, true)}</dd>
              </div>
              <div>
                <dt>Neto</dt>
                {/* La resta se hace aquí: pedirla sería una cifra más que puede discrepar. */}
                <dd>
                  <strong>{conSigno(movimientoNeto(m), euros)}</strong>
                </dd>
              </div>
            </dl>

            {m.by_plan.length > 0 && (
              <div className="tabla-marco">
                <table className="tabla">
                  <thead>
                    <tr>
                      <th>Plan</th>
                      <th className="num">Clubes</th>
                      <th className="num">MRR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {m.by_plan.map((p) => (
                      <tr key={p.plan_code}>
                        <td>
                          <code>{p.plan_code}</code>
                        </td>
                        <td className="num">{p.clubs}</td>
                        <td className="num">{euros(p.mrr_cents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </Bloque>

      <Bloque
        titulo="Bajas por cohorte"
        explicacion="Por el mes del alta, que es la única forma de ver si los clubes que entraron en marzo aguantan más que los de enero: si el producto mejora, o si solo hay más clubes."
        consulta={churn}
        silenciarHueco={sinPasada}
      >
        {(c) =>
          c.cohorts.length === 0 ? (
            <p className="tenue">
              Ninguna cohorte todavía. Hacen falta clubes con unos meses detrás.
            </p>
          ) : (
            <div className="tabla-marco">
              <table className="tabla">
                <thead>
                  <tr>
                    <th>Entraron en</th>
                    <th className="num">Cuántos</th>
                    <th className="num">Siguen</th>
                    <th className="num">Se fueron</th>
                    <th className="num">Se va el</th>
                  </tr>
                </thead>
                <tbody>
                  {c.cohorts.map((co) => (
                    <tr key={co.cohort}>
                      <td className="mes-suelto">{mesEnPalabras(co.cohort)}</td>
                      <td className="num">{co.size}</td>
                      <td className="num">{co.alive}</td>
                      <td className="num">{co.churned}</td>
                      <td className="num">
                        {numero(co.churn_rate * 100, 0)} %
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </Bloque>

      <Bloque
        titulo="Alumnos en la plataforma"
        consulta={alumnos}
        silenciarHueco={sinPasada}
      >
        {(a) => (
          <dl className="datos datos-grandes">
            <div>
              <dt>En total</dt>
              <dd>{a.students}</dd>
            </div>
            <div>
              <dt>Clubes con alumnos</dt>
              <dd>{a.academies_with_students}</dd>
            </div>
            <div>
              <dt>Media por club</dt>
              <dd>{numero(a.avg_per_academy)}</dd>
            </div>
          </dl>
        )}
      </Bloque>

      <Bloque
        titulo="De dónde salen los clubes"
        explicacion="Conversión por canal, desde el origen que se anotó en cada lead."
        consulta={adquisicion}
        silenciarHueco={sinPasada}
      >
        {(a) =>
          a.channels.length === 0 ? (
            <p className="tenue">Ningún lead con origen anotado este mes.</p>
          ) : (
            <>
              <div className="tabla-marco">
                <table className="tabla">
                  <thead>
                    <tr>
                      <th>Canal</th>
                      <th className="num">Leads</th>
                      <th className="num">Ganados</th>
                      <th className="num">Perdidos</th>
                      <th className="num">Abiertos</th>
                      <th className="num">Convierte el</th>
                      <th className="num">CAC</th>
                    </tr>
                  </thead>
                  <tbody>
                    {a.channels.map((ca) => (
                      <tr key={ca.source}>
                        <td>{nombreDeCanal(ca.source)}</td>
                        <td className="num">{ca.leads}</td>
                        <td className="num">{ca.won}</td>
                        <td className="num">{ca.lost}</td>
                        <td className="num">{ca.open}</td>
                        <td className="num">
                          {numero(ca.conversion_rate * 100, 0)} %
                        </td>
                        <td className="num">
                          {/*
                            **Siempre vacío, y no es un fallo.** No hay ninguna
                            tabla de gasto de marketing en el esquema: poner un
                            número aquí sería inventárselo, y un CAC inventado
                            acaba en un correo a inversores.
                          */}
                          {ca.cac_cents === null ? (
                            <span className="tenue">—</span>
                          ) : (
                            euros(ca.cac_cents)
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p className="tenue">
                <strong>La columna del CAC está vacía a propósito.</strong> Para
                calcularlo hace falta el gasto de marketing por canal, y eso no
                se guarda en ningún sitio: un CAC inventado acaba en un correo a
                inversores. La conversión sí sale de datos reales.
              </p>
            </>
          )
        }
      </Bloque>

      <Bloque
        titulo="Extras"
        explicacion="Material entregado e inscripciones a competición."
        consulta={extras}
        silenciarHueco={sinPasada}
      >
        {(e) => (
          <>
            <dl className="datos datos-grandes">
              <div>
                <dt>Volumen del mes</dt>
                <dd>{euros(e.total_volume_cents)}</dd>
              </div>
            </dl>

            {e.lines.length > 0 && (
              <div className="tabla-marco">
                <table className="tabla">
                  <thead>
                    <tr>
                      <th>Concepto</th>
                      <th className="num">Volumen</th>
                      <th className="num">Operaciones</th>
                      <th className="num">Clubes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {e.lines.map((l) => (
                      <tr key={l.kind}>
                        <td>{etiquetaDeExtra(l.kind)}</td>
                        <td className="num">{euros(l.volume_cents)}</td>
                        <td className="num">{l.count}</td>
                        <td className="num">{l.academies}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <p className="tenue">
              <strong>
                Esto es volumen que pasa por la plataforma, no nuestro ingreso.
              </strong>{' '}
              No hay ninguna tasa de comisión configurada, así que ponerle un
              porcentaje sería inventarse la cifra. Lo que se puede medir es lo
              entregado y lo inscrito, y eso es lo que hay.
            </p>
          </>
        )}
      </Bloque>
    </section>
  );
}
